import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_AVAILABILITY, E164, MAX_SERVICE_ANSWER_LENGTH, usableCustomerName } from '@acs/shared';
import { Banner, Button, Field, Icon, Input, Modal, Select, Textarea } from '@acs/ui';
import { useI18n, type TranslationKey } from '../../i18n/index.js';
import { ownerBookingRefusal } from '../../shared/api/appointments.js';
import type { BusinessDoc, ClientDoc, StaffDoc } from '../../shared/api/converters.js';
import { friendlyError } from '../../shared/api/errors.js';
import { useClients } from '../clients/hooks.js';
import { DIAL_PREFIX, normalizePhone } from '../clients/phoneInput.js';
import { addDays, labelToMin, mondayOf, todayIsoIn, zonedDateAndMinute } from '../schedule-grid/index.js';
import { useAvailability, useServices } from '../settings/hooks.js';
import { useStaff } from '../staff/hooks.js';
import {
  allDayTimes,
  asOptions,
  blockedTimes,
  bookingGrid,
  bookingsOn,
  capacityFor,
  endAfter,
  endOptions,
  firstFreeStart,
  followStart,
  isDate,
  isTime,
  outsideHours,
  phoneSuggestions,
  rangeMin,
  slotProblem,
  slotVerdict,
  startIsGone,
  startOptions,
  suggestionCutoff,
  timeRangeProblem,
} from './bookingForm.js';
import { DiscardChangesDialog } from './DiscardChangesDialog.js';
import { useCreateManualBooking, useWeekAppointments } from './hooks.js';

/** Value of the staff select meaning "whoever is free" (doc 43 D7). */
const ANY = '';

/** How far past the opening date the form looks for a free time (doc 86). */
const LOOKAHEAD_DAYS = 60;

/** What can be wrong with a field: shown once the owner changed it, or after a
 * submit (doc 86). `when` is the date, times, service and staff together — a
 * new date can make the untouched start taken. */
type FieldKey = 'phone' | 'name' | 'when' | `q:${string}`;

/** Staff who perform a service (doc 09 D6): empty `serviceIds` = all of them. */
const offers = (s: StaffDoc, serviceId: string) => s.serviceIds.length === 0 || s.serviceIds.includes(serviceId);

interface Draft {
  phone: string;
  name: string;
  serviceId: string;
  staffId: string;
  date: string;
  start: string;
  end: string;
  note: string;
}

/**
 * The owner books a customer by hand (doc 43). The phone field searches the
 * client base as it is typed; picking a match fills the name. Closing with
 * anything typed asks first (D9). Times the book does not allow — taken, or
 * with nobody on the team working — are disabled; outside the working hours of
 * a business without staff, a warning (doc 85).
 */
export function NewBookingDialog({
  business,
  date: openOn,
  onClose,
}: {
  business: BusinessDoc;
  /** The day the dashboard shows (doc 86; see `newBookingDate`). Today when absent. */
  date?: string;
  onClose(): void;
}) {
  const { t } = useI18n();
  const create = useCreateManualBooking();
  const { data: services } = useServices(business.id);
  const { data: staff } = useStaff(business.id);
  const { data: clients } = useClients(business.id);

  const prefix = DIAL_PREFIX[business.country] ?? '+';
  const activeServices = useMemo(() => (services ?? []).filter((s) => s.active), [services]);
  const activeStaff = useMemo(() => (staff ?? []).filter((s) => s.active), [staff]);

  const { data: avail } = useAvailability(business.id);
  const availability = avail ?? DEFAULT_AVAILABILITY;
  const today = todayIsoIn(business.timezone);
  const nowMin = zonedDateAndMinute(new Date(), business.timezone).min;
  /** The day the form opened on, and the last it looks at for a free time. */
  const [base] = useState(() => (openOn && isDate(openOn) && openOn > today ? openOn : today));
  const lastDay = addDays(base, LOOKAHEAD_DAYS);
  /** Until the owner touches the date, times, service or staff, the form keeps
   * choosing the time itself — refining its first guess as the hours, the team
   * and the book arrive — and stops for good once the book confirms one. */
  const auto = useRef(true);
  /** What the current guess was made with: a new service or the real opening
   * hours make a new one, from the opening date again. */
  const placedWith = useRef('');
  /** Bumped by every placement, so the next look at the book runs even when
   * the book itself was already there and nothing else it reads changed. */
  const [placements, setPlacements] = useState(0);
  const [initial, setInitial] = useState<Draft>(() => blankDraft(prefix, base));
  const [draft, setDraft] = useState<Draft>(initial);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<FieldKey>>(() => new Set());
  const shown = (key: FieldKey) => submitted || touched.has(key);
  const touch = (key: FieldKey) => {
    if (key === 'when') auto.current = false;
    setTouched((s) => (s.has(key) ? s : new Set(s).add(key)));
  };
  const [suggestOpen, setSuggestOpen] = useState(true);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [discarding, setDiscarding] = useState(false);
  /**
   * Answers to the service's questions (doc 64), by question id. Emptied when
   * the service changes — another service asks other things — and kept across
   * date, staff and refused retries. Matched by id against the service as it
   * is NOW (a live subscription): a reworded question has a new id, so it
   * starts empty and blocks booking until answered.
   */
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const suggestRef = useRef<HTMLUListElement>(null);

  // Until the owner picks one, the first service — the same default the
  // services list leads with — so the end time has a duration to come from.
  const serviceId = draft.serviceId || activeServices[0]?.id || '';
  const service = activeServices.find((s) => s.id === serviceId) ?? null;
  const eligibleStaff = activeStaff.filter((s) => offers(s, serviceId));
  const staffId = eligibleStaff.some((s) => s.id === draft.staffId) ? draft.staffId : ANY;
  const member = eligibleStaff.find((s) => s.id === staffId) ?? null;
  const end =
    draft.end ||
    (service
      ? service.allDay
        ? (allDayTimes(availability, draft.date, member)?.end ?? '')
        : endAfter(draft.start, service.durationMin)
      : '');

  /** The times a service brings with it: an all-day one (doc 45) takes the
   * opening to closing of whoever takes it that day — the chosen member, or
   * the business (doc 85); a timed one keeps the start and gets its length. */
  const timesFor = (
    next: { allDay: boolean; durationMin: number },
    date: string,
    start: string,
    who: StaffDoc | null,
  ) => {
    if (!next.allDay) return { start, end: endAfter(start, next.durationMin) };
    const day = allDayTimes(availability, date, who);
    return { start: day?.start ?? start, end: day?.end ?? '' };
  };

  const phone = normalizePhone(draft.phone);
  const phoneValid = E164.safeParse(phone).success;
  const known = clients?.find((c) => c.phone === phone) ?? null;
  const suggestions = suggestOpen && !known ? phoneSuggestions(clients ?? [], draft.phone, prefix) : [];

  // Times are offered on the business's booking step, aligned like the
  // assistant's slots, never per minute (D12).
  const grid = bookingGrid(business.slotGranularityMin, availability, draft.date);
  const starts = startOptions(grid, draft.start, draft.date === today ? nowMin : -1);
  const startGone = startIsGone(draft.date, draft.start, today, nowMin);
  const timeProblem = timeRangeProblem(draft.start, end);

  // What the book allows (doc 85): the bookings that day and who could take
  // this one. Null until both have arrived — nothing is ruled out before
  // that, and the server has the last word anyway.
  const { data: appointments } = useWeekAppointments(business.id, mondayOf(isDate(draft.date) ? draft.date : today));
  const capacity =
    staff === undefined || appointments === undefined
      ? null
      : capacityFor(
          activeStaff.length === 0 ? null : member ? [member] : eligibleStaff,
          availability,
          draft.date,
          bookingsOn(appointments, draft.date, business.timezone),
        );
  const allDay = service?.allDay ?? false;
  // The length the booking has: the one shown, or the service's before there
  // is a range to measure.
  const blocked = blockedTimes(capacity, draft.start, rangeMin(draft.start, end) || (service?.durationMin ?? 0), allDay);
  const verdict =
    capacity && timeProblem === null ? slotVerdict(capacity, labelToMin(draft.start), labelToMin(end), allDay) : 'free';
  const slotError = slotProblem(capacity, verdict, member?.name ?? null);
  const noStaff = capacity !== null && capacity.staffed && capacity.members.length === 0;
  const offHours =
    capacity !== null && !capacity.staffed && timeProblem === null && outsideHours(availability, draft.date, draft.start, end, allDay);
  const questions = service?.questions ?? [];
  const answerOf = (id: string) => (answers[id] ?? '').trim();
  const answersComplete = questions.every((q) => answerOf(q.id).length > 0 && answerOf(q.id).length <= MAX_SERVICE_ANSWER_LENGTH);
  const dirty =
    (Object.keys(initial) as (keyof Draft)[]).some((k) => draft[k] !== initial[k]) ||
    Object.values(answers).some((a) => a.trim().length > 0);
  const canSubmit =
    phoneValid &&
    // The same name rule the server applies (doc 65): a real name, ≤ 120.
    usableCustomerName(draft.name) !== null &&
    !!service &&
    answersComplete &&
    !startGone &&
    timeProblem === null &&
    verdict === 'free' &&
    !create.isPending;
  const nameError = shown('name') && usableCustomerName(draft.name) === null ? t('bookingForm.nameRequired') : undefined;
  // While typing, only once there is more than the dial prefix; after a
  // submit, an empty number too.
  const phoneTyped = draft.phone.trim() !== '' && draft.phone.trim() !== prefix;
  const phoneError = shown('phone') && !phoneValid && (phoneTyped || submitted) ? t('addClient.phoneHelp') : undefined;

  // The suggestion (doc 86): the first free time from the opening date on —
  // from now plus the booking notice today, the next working day once today
  // has nothing left. A first guess from the opening hours alone, written into
  // the initial state too so the form is not "dirty" for being filled in by
  // us; once the book for that day has arrived, a start it rules out moves to
  // the first free one, or the guess moves on a day. Updaters, not values:
  // only the time fields — never what the owner is typing meanwhile.
  const defaultService = activeServices[0];
  const hoursLoaded = avail !== undefined;
  const cutoff = suggestionCutoff(today, nowMin, availability);
  const startOn = (date: string, capacityThen: typeof capacity) =>
    firstFreeStart({
      availability,
      stepMin: business.slotGranularityMin,
      date,
      afterMin: date === cutoff.date ? cutoff.afterMin : date < cutoff.date ? 24 * 60 : -1,
      lengthMin: service?.durationMin ?? 0,
      allDay,
      member,
      capacity: capacityThen,
    });
  /** The time fields for `start` on `date` — the service's length, or an
   * all-day service's opening to closing. */
  const timesAt = (date: string, start: string) =>
    !start ? { start: '', end: '' } : service ? timesFor(service, date, start, member) : { start, end: '' };
  /** The first day from `from` the opening hours leave a time on. */
  const guessFrom = (from: string): Pick<Draft, 'date' | 'start' | 'end'> => {
    for (let date = from < cutoff.date ? cutoff.date : from; date <= lastDay; date = addDays(date, 1)) {
      const start = startOn(date, null);
      if (start) return { date, ...timesAt(date, start) };
    }
    return { date: from, start: '', end: '' };
  };
  const place = (patch: Partial<Draft>) => {
    const apply = (d: Draft): Draft => ({ ...d, ...patch });
    setInitial(apply);
    setDraft(apply);
    setPlacements((n) => n + 1);
  };
  const placeKey = `${hoursLoaded}|${defaultService?.id ?? ''}`;
  // Before paint, so the form never flashes empty times.
  useLayoutEffect(() => {
    if (!auto.current) return;
    if (placedWith.current !== placeKey) {
      placedWith.current = placeKey;
      place({ serviceId: defaultService?.id ?? '', ...guessFrom(base) });
      return;
    }
    // Only real hours and a real book settle it; until then the guess stands.
    if (!hoursLoaded || capacity === null) return;
    // Nobody offers the service: no day will have a time. The start says why.
    if (noStaff) {
      auto.current = false;
      return;
    }
    const start = startOn(draft.date, capacity);
    if (start) {
      auto.current = false;
      if (start !== draft.start) place(timesAt(draft.date, start));
      return;
    }
    const next = addDays(draft.date, 1);
    if (next > lastDay) {
      // Nothing free for two months: the owner picks, starting from the day
      // they opened on.
      auto.current = false;
      place({ date: base, start: '', end: '' });
      return;
    }
    place(guessFrom(next));
    // Re-run after each placement and as each piece arrives: the hours, the
    // default service, the day's book (capacity), the team.
  }, [placeKey, placements, capacity === null, appointments, staff]);

  /** Every edit goes through here: an answer the server gave about the OLD
   * values must not stay on screen for the new ones. */
  const edit = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setRefusal(null);
    create.reset();
  };

  const answer = (questionId: string, text: string) => {
    setAnswers((a) => ({ ...a, [questionId]: text }));
    setRefusal(null);
    create.reset();
  };

  /** Moving the date to today must not leave a start that has already gone
   * selected — it would only be refused on submit (D6). */
  const changeDate = (date: string) => {
    let start = draft.start;
    if (date === today && startIsGone(date, start, today, nowMin)) {
      const g = bookingGrid(business.slotGranularityMin, availability, date);
      start = startOptions(g, '', nowMin)[0] ?? '';
    }
    if (service?.allDay) edit({ date, ...timesFor(service, date, start, member) });
    else edit({ date, start, end: followStart(draft.start, end, start, service?.durationMin) });
  };

  const pick = (client: ClientDoc) => {
    edit({ phone: client.phone, name: client.name });
    setSuggestOpen(false);
  };

  const requestClose = () => {
    if (create.isPending) return;
    if (dirty) setDiscarding(true);
    else onClose();
  };

  const submit = () => {
    if (!canSubmit || !service) {
      // Say what is missing, on every field — untouched ones included.
      setSubmitted(true);
      return;
    }
    create.mutate(
      {
        businessId: business.id,
        serviceId: service.id,
        staffId: staffId === ANY ? null : staffId,
        customerPhone: phone,
        customerName: draft.name.trim(),
        note: draft.note.trim(),
        // Only the questions the service asks now, in its order.
        serviceAnswers: questions.map((q) => ({ questionId: q.id, answer: answerOf(q.id) })),
        date: draft.date,
        startTime: draft.start,
        endTime: end,
      },
      {
        onSuccess: onClose,
        onError: (err) => {
          // Taken meanwhile, a schedule changed in another tab…: said once,
          // and the live book disables the time on its own.
          const reason = ownerBookingRefusal(err);
          if (reason) setRefusal(t(`bookingForm.refusal.${reason}` as TranslationKey));
        },
      },
    );
  };

  // "Missing" is only worth saying once there is a start: then it means the
  // service would run past midnight and the end was left for the owner.
  const timeError =
    shown('when') && service && timeProblem && (timeProblem !== 'missing' || isTime(draft.start))
      ? t(`bookingForm.time.${timeProblem}` as TranslationKey)
      : undefined;
  // Not about anything the owner typed, so said straight away.
  const startError = noStaff
    ? t('bookingForm.refusal.no_staff')
    : !shown('when')
      ? undefined
      : startGone && draft.date === today
        ? t('bookingForm.refusal.in_past')
        : slotError
          ? t(slotError.key, { name: slotError.name ?? '' })
          : !isTime(draft.start)
            ? t('bookingForm.startRequired')
            : undefined;

  return (
    <Modal
      closeLabel={t('ui.closeDialog')}
      title={t('bookingForm.newTitle')}
      // Escape and the backdrop are document-level in both overlays: while the
      // discard question is up, they must answer only that.
      onClose={discarding ? () => {} : requestClose}
      footer={
        <>
          <Button variant="secondary" onClick={requestClose} disabled={create.isPending}>
            {t('common.cancel')}
          </Button>
          {/* Always pressable: a press with something missing shows what. */}
          <Button disabled={create.isPending} loading={create.isPending} onClick={submit}>
            {t('bookingForm.create')}
          </Button>
        </>
      }
    >
      <div className="booking-form">
        <Field
          label={t('booking.phone')}
          required
          hint={t('bookingForm.phoneHint')}
          error={phoneError}
        >
          <Input
            autoFocus
            mono
            inputMode="tel"
            autoComplete="off"
            value={draft.phone}
            placeholder={t('addClient.phonePlaceholder')}
            aria-autocomplete="list"
            aria-controls={suggestions.length ? 'booking-client-suggest' : undefined}
            onChange={(e) => {
              edit({ phone: e.target.value });
              touch('phone');
              setSuggestOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' && suggestions.length) {
                e.preventDefault();
                suggestRef.current?.querySelector('button')?.focus();
              }
            }}
          />
        </Field>
        {suggestions.length > 0 && (
          <ul
            id="booking-client-suggest"
            ref={suggestRef}
            className="client-suggest"
            aria-label={t('bookingForm.suggestions')}
            onKeyDown={(e) => {
              if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
              e.preventDefault();
              const buttons = [...(suggestRef.current?.querySelectorAll('button') ?? [])];
              const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
              buttons[Math.max(0, Math.min(buttons.length - 1, at + (e.key === 'ArrowDown' ? 1 : -1)))]?.focus();
            }}
          >
            {suggestions.map((c) => (
              <li key={c.id}>
                <button type="button" className="client-suggest__item" onClick={() => pick(c)}>
                  <Icon name="user" size={15} />
                  <span className="client-suggest__name">{c.name}</span>
                  <span className="client-suggest__phone mono">{c.phone}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <Field
          label={t('booking.customer')}
          required
          error={nameError}
          hint={
            known
              ? known.name === draft.name.trim()
                ? t('bookingForm.knownClient')
                : t('bookingForm.knownClientAs', { name: known.name })
              : undefined
          }
        >
          <Input
            value={draft.name}
            maxLength={120}
            placeholder={t('addClient.namePlaceholder')}
            onChange={(e) => {
              edit({ name: e.target.value });
              touch('name');
            }}
          />
        </Field>

        {activeServices.length === 0 ? (
          <Banner tone="warning">{t('bookingForm.noServices')}</Banner>
        ) : (
          <Field label={t('booking.service')} required>
            <Select
              value={serviceId}
              onChange={(e) => {
                const next = activeServices.find((s) => s.id === e.target.value);
                const who = next ? (activeStaff.find((s) => s.id === draft.staffId && offers(s, next.id)) ?? null) : null;
                // A different service has a different length: the end follows
                // (and an all-day one takes the whole day). Its questions are
                // its own: answers never carry over.
                edit({ serviceId: e.target.value, ...(next ? timesFor(next, draft.date, draft.start, who) : {}) });
                touch('when');
                if (e.target.value !== serviceId) setAnswers({});
              }}
              options={activeServices.map((s) => ({
                value: s.id,
                label: `${s.name} · ${s.allDay ? t('services.allDay') : t('services.minutesShort', { count: s.durationMin })}`,
              }))}
            />
          </Field>
        )}

        {activeStaff.length > 0 && (
          <Field label={t('booking.staff')}>
            <Select
              value={staffId}
              onChange={(e) => {
                const who = eligibleStaff.find((s) => s.id === e.target.value) ?? null;
                // An all-day booking is the opening to closing of whoever takes it.
                edit({ staffId: e.target.value, ...(service?.allDay ? timesFor(service, draft.date, draft.start, who) : {}) });
                touch('when');
              }}
              options={[
                { value: ANY, label: t('bookingForm.anyStaff') },
                ...eligibleStaff.map((s) => ({ value: s.id, label: s.name })),
              ]}
            />
          </Field>
        )}

        <div className="booking-form__when">
          <Field
            label={t('bookingForm.date')}
            required
            error={shown('when') && draft.date && draft.date < today ? t('bookingForm.refusal.in_past') : undefined}
          >
            <Input
              type="date"
              min={today}
              value={draft.date}
              onChange={(e) => {
                changeDate(e.target.value);
                touch('when');
              }}
            />
          </Field>
          <Field label={t('bookingForm.start')} required error={startError}>
            <Select
              value={draft.start}
              placeholder="—"
              options={asOptions(starts, blocked.start)}
              onChange={(e) => {
                edit({ start: e.target.value, end: followStart(draft.start, end, e.target.value, service?.durationMin) });
                touch('when');
              }}
            />
          </Field>
          <Field label={t('bookingForm.end')} required error={timeError}>
            <Select
              value={end}
              placeholder="—"
              options={asOptions(endOptions(grid, draft.start, end), blocked.end)}
              onChange={(e) => {
                edit({ end: e.target.value });
                touch('when');
              }}
            />
          </Field>
        </div>
        {offHours && <Banner tone="warning">{t('bookingForm.outsideHours')}</Banner>}

        {questions.length > 0 && (
          <fieldset className="booking-form__questions">
            <legend className="booking-form__title">{t('bookingForm.questionsTitle')}</legend>
            <p className="muted booking-form__hint">{t('bookingForm.questionsHint')}</p>
            {questions.map((q) => {
              const given = answerOf(q.id).length;
              const error = !shown(`q:${q.id}`)
                ? undefined
                : given === 0
                  ? t('bookingForm.answerRequired')
                  : given > MAX_SERVICE_ANSWER_LENGTH
                    ? t('bookingForm.answerTooLong', { max: MAX_SERVICE_ANSWER_LENGTH })
                    : undefined;
              return (
                <Field key={q.id} label={q.text} required error={error}>
                  <Textarea
                    rows={2}
                    value={answers[q.id] ?? ''}
                    onChange={(e) => {
                      answer(q.id, e.target.value);
                      touch(`q:${q.id}`);
                    }}
                  />
                </Field>
              );
            })}
          </fieldset>
        )}

        <Field label={t('bookingForm.note')} optional>
          <Textarea
            rows={2}
            maxLength={300}
            value={draft.note}
            placeholder={t('bookingForm.notePlaceholder')}
            onChange={(e) => edit({ note: e.target.value })}
          />
        </Field>

        {refusal && <Banner tone="danger">{refusal}</Banner>}
        {create.isError && !refusal && (
          <Banner tone="danger">{friendlyError(t, create.error, t('bookingForm.failed'))}</Banner>
        )}
      </div>

      {discarding && <DiscardChangesDialog onKeep={() => setDiscarding(false)} onDiscard={onClose} />}
    </Modal>
  );
}

/** A fresh form on `date`: the dial prefix, no times yet — the suggestion
 * fills those (doc 86). */
function blankDraft(prefix: string, date: string): Draft {
  return { phone: prefix, name: '', serviceId: '', staffId: ANY, date, start: '', end: '', note: '' };
}
